/*
 * Copyright (c) 2016 Omnigon Communications, LLC. All rights reserved.
 *
 * This software is the confidential and proprietary information of Omnigon Communications, LLC
 * ("Confidential Information"). You shall not disclose such Confidential Information and shall access and use it only
 * in accordance with the terms of the license agreement you entered into with Omnigon Communications, LLC, its
 * subsidiaries, affiliates or authorized licensee. Unless required by applicable law or agreed to in writing, this
 * Confidential Information is provided on an "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either
 * express or implied. See the license agreement for the specific language governing permissions and limitations.
 */

import _ from 'lodash';
import can from 'can';
import Model from 'can/model/model';
import List from 'scripts/models/base/list';
import Map from 'scripts/models/base/map';
import Standing from './standing.model';
import compare from 'vendors/compare-property/compare-property';

const Standings = List.extend({
    Map: Standing
}, {
    isDisplayValueEmpty(item, prop) {
        const isEmpty = ['0', '', '-', 'WD', 'DQ'].indexOf(item.attr(`${prop}.displayValue`)) > -1;

        return isEmpty;
    },

    setSortingHelpers() {
        this.each((item) => {
            item.attr('notEmptyThru', this.isDisplayValueEmpty(item, 'holesThrough') ? 0 : 1);
            item.attr('notEmptyRd0', this.isDisplayValueEmpty(item, 'roundScores.0.score') ? 0 : 1);
            item.attr('notEmptyRd1', this.isDisplayValueEmpty(item, 'roundScores.1.score') ? 0 : 1);
        });
    }
});

function invertOrdering(ordering) {
    return ordering === 1 ? -1 : 1;
}

function getSortingFunc(sortBy, sortAsc) {
    const sortProperty = sortBy;
    const ordering = sortAsc ? 1 : -1;
    const properties = {};
    properties['notEmptyThru'] = -1;

    switch (sortProperty) {
        case 'player.name':
            return (a, b) => {
                let aVal = `${_.get(a, 'player.firstName', '')} ${_.get(a, 'player.lastName', '')}`;
                let bVal = `${_.get(b, 'player.firstName', '')} ${_.get(b, 'player.lastName', '')}`;
                
                return compare.compare(aVal, bVal, ordering, true);
            };
        case 'holesThrough.value':
            properties[sortProperty] = ordering;
            properties['player.teeTime'] = ordering;
            properties['player.course'] = ordering;
            properties['player.startingTee'] = ordering;

            return compare.properties(properties);
        case 'roundScores.0.score.value':
            properties['notEmptyRd0'] = invertOrdering(ordering);
            properties[sortProperty] = ordering;

            return compare.properties(properties);
        case 'roundScores.1.score.value':
            properties['notEmptyRd1'] = invertOrdering(ordering);
            properties[sortProperty] = ordering;

            return compare.properties(properties);
        case 'toPar.value':
        case 'position.value':
            properties[sortProperty] = ordering;
            properties['player.middleName'] = ordering;
            properties['player.teeTime'] = ordering;
            properties['player.course'] = ordering;
            properties['player.startingTee'] = ordering;

            return compare.properties(properties);
        default:
            return compare.property(sortProperty, ordering);
    }
};

export default Model.extend({}, {
    define: {
        fetching: {
            type: 'boolean',
            value: true
        },

        sortBy: {
            type: 'string',
            value: 'position.value'
        },

        sortByKey: {
            type: 'string',
            value: 'pos'
        },

        sortAsc: {
            type: 'boolean',
            value: true
        },

        favoritesIds: {
            Type: can.List,
            Value: can.List
        },

        hasFavorite: {
            type: 'boolean',
            value: false
        },

        amateurOnly: {
            type: 'boolean',
            value: false
        },

        currentRound: {
            type: 'number',
            value: 1
        },

        visiblePlayersCount: {
            type: 'number',
            value: 0
        },

        standings: {
            Type: Standings,
            Value: Standings,
            set(standings) {
                standings.attr('comparator', getSortingFunc(this.attr('sortBy'), this.attr('sortAsc')));
                return standings;
            }
        },

        logo: {
            Type: Map,
            value: null
        },

        legend: {
            type: 'string',
            value: ''
        },

        filterStangings: {
            get() {
                can.batch.start();
                const standings = this.attr('standings');
                const amateurOnly = this.attr('amateurOnly');
                const favoritesIds = this.attr('favoritesIds') || [];
                const sortBy = this.attr('sortBy');
                const sortByKey = this.attr('sortByKey');
                const sortAsc = this.attr('sortAsc');
                const cutIsProjected = ('' + this.attr('cutLine.projected')) === 'true';
                const cutScore = parseInt(this.attr('cutLine.score'));
                const showPlayersUnderCut = this.attr('showPlayersUnderCut');
                let visiblePlayersCount = 0;
                this.attr('hasFavorite', false);

                standings.forEach((standing) => {
                    let visible = true;
                    let isPlayerUnderCut = false;
                    let toPar = parseInt(standing.attr('toPar.value'));

                    if (amateurOnly) {
                        visible = standing.player.isAmateur === amateurOnly;
                    }

                    if(visible) {
                        visiblePlayersCount++;
                    }

                    const isFavorite = favoritesIds.indexOf(standing.player.identifier) !== -1;
                    if (isFavorite) {
                        this.attr('hasFavorite', true);
                    }

                    if (showPlayersUnderCut) {
                        if (cutIsProjected && !isNaN(cutScore) && !isNaN(toPar)) {
                            isPlayerUnderCut = (toPar <= cutScore) === false || standing.isPlayerDisqualified();
                        }
                        else {
                            isPlayerUnderCut = ['WD', 'DQ', 'MC'].indexOf(standing.attr('position.displayValue')) > -1;
                        }
                    }

                    standing.attr('isPlayerUnderCut', isPlayerUnderCut);
                    standing.attr('visible', visible);
                    standing.attr('player').attr('isFavorite', isFavorite);
                    standing.attr('sortByKey', sortByKey);
                });
                this.attr('visiblePlayersCount', visiblePlayersCount);
                can.batch.stop();

                return standings;
            }
        },

        showPlayersUnderCut: {
            get() {
                let sortByPosOrPar = ['pos', 'to par'].indexOf(this.attr('sortByKey')) > -1;

                return sortByPosOrPar && this.attr('sortAsc') && this.attr('cutLine');
            }
        }
    },

    isDrawerDisabled(standing) {
        return standing.attr('player.currentRound.number') === 1 && 
            ['DQ', 'WD'].indexOf(standing.attr('roundScores.0.score.displayValue')) > -1;
    },

    sortList(sortBy, sortByKey) {
        can.batch.start();
        if (sortByKey === this.attr('sortByKey')) {
            this.attr('sortAsc', !this.attr('sortAsc'));
        } else {
            this.attr('sortAsc', true);
            this.attr('sortBy', sortBy);
            this.attr('sortByKey', sortByKey);
        }
        this.attr('standings.comparator', getSortingFunc(sortBy, this.attr('sortAsc')));
        can.batch.stop();
    },

    filterAmateurs(value) {
        this.attr('amateurOnly', value);
    }
});
